import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Dropdown, Space, Avatar, Badge } from 'antd';
import {
    DashboardOutlined,
    UserOutlined,
    MedicineBoxOutlined,
    FileTextOutlined,
    LogoutOutlined,
    GlobalOutlined,
    TeamOutlined,
    BellOutlined,
    SettingOutlined,
    CalendarOutlined,
    BarChartOutlined,
    DollarOutlined,
    FileProtectOutlined,
    ExperimentOutlined,
    ScissorOutlined,
    MenuOutlined,
    PictureOutlined,
    BankOutlined,
    DatabaseOutlined,
    HistoryOutlined,
    EyeOutlined
} from '@ant-design/icons';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import OfflineBanner from './OfflineBanner';
import { settingsService } from '../services';
import BrandLogo, { BRAND_NAME } from './BrandLogo';
import MobileTabBar, { tabFor } from './MobileTabBar';
import './MainLayout.css';

const { Header, Sider, Content } = Layout;

const MainLayout = ({ role }) => {
    const [collapsed, setCollapsed] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    const { t, i18n } = useTranslation();
    // Whether this clinic is a dental one: the dentist's own account, or a
    // doctor who has switched the dental section on. Decides the Dental entry
    // in the menu, for the doctor and for their secretary alike.
    const [dentalClinic, setDentalClinic] = useState(user?.doctorType === 'dentist');

    useEffect(() => {
        if (role !== 'doctor' && role !== 'secretary') return undefined;
        if (user?.doctorType === 'dentist') {
            setDentalClinic(true);
            return undefined;
        }

        const doctorId = role === 'doctor' ? user?._id : user?.doctorId;
        if (!doctorId) return undefined;

        let current = true;
        // Best-effort: a settings problem costs a menu entry, not the page.
        settingsService.getSettings(doctorId)
            .then((settings) => { if (current) setDentalClinic(settings?.visitFields?.dental === true); })
            .catch(() => {});
        return () => { current = false; };
    }, [role, user?._id, user?.doctorId, user?.doctorType]);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (mobile) {
                setCollapsed(true); // Collapse sidebar on mobile by default
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Check on mount

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Initialize direction on mount
    useEffect(() => {
        const savedLang = localStorage.getItem('language') || user?.language || 'en';
        i18n.changeLanguage(savedLang);
        const isRTL = savedLang === 'ar' || savedLang === 'ku';
        document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
        document.body.dir = isRTL ? 'rtl' : 'ltr';
    }, []);

    useEffect(() => {
        if (user?.language && !localStorage.getItem('language')) {
            i18n.changeLanguage(user.language);
            const isRTL = user.language === 'ar' || user.language === 'ku';
            document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
            document.body.dir = isRTL ? 'rtl' : 'ltr';
        }
    }, [user, i18n]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const changeLanguage = (lang) => {
        i18n.changeLanguage(lang);
        localStorage.setItem('language', lang);
        const isRTL = lang === 'ar' || lang === 'ku';
        document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
        document.body.dir = isRTL ? 'rtl' : 'ltr';
        // Force re-render by reloading
        window.location.reload();
    };

    const languageMenu = (
        <Menu
            onClick={({ key }) => changeLanguage(key)}
            items={[
                { key: 'en', label: t('english') },
                { key: 'ar', label: t('arabic') },
                { key: 'ku', label: t('kurdish') }
            ]}
        />
    );

    const getMenuItems = () => {
        const baseItems = [
            {
                key: `/${role}/dashboard`,
                icon: <DashboardOutlined />,
                label: t('dashboard')
            }
        ];

        if (role === 'superadmin') {
            return [
                ...baseItems,
                {
                    key: `/${role}/doctors`,
                    icon: <UserOutlined />,
                    label: t('doctors')
                },
                {
                    key: `/${role}/hospitals`,
                    icon: <BankOutlined />,
                    label: t('hospitals') || 'Hospitals'
                },
                {
                    key: `/${role}/companysellers`,
                    icon: <TeamOutlined />,
                    label: t('companySellers') || 'Company Sellers'
                },
                {
                    key: `/${role}/reklam-sliders`,
                    icon: <PictureOutlined />,
                    label: 'Reklam Sliders'
                },
                // {
                //     key: `/${role}/settings`,
                //     icon: <SettingOutlined />,
                //     label: t('settings')
                // },
                {
                    key: `/${role}/reports`,
                    icon: <FileTextOutlined />,
                    label: t('reports')
                }
            ];
        }

        if (role === 'doctor') {
            return [
                ...baseItems,
                {
                    key: `/${role}/appointments`,
                    icon: <CalendarOutlined />,
                    label: t('appointments')
                },
                {
                    key: `/${role}/appointments-report`,
                    icon: <FileTextOutlined />,
                    label: t('appointmentsReport')
                },
                {
                    key: `/${role}/patients`,
                    icon: <UserOutlined />,
                    label: t('patients')
                },
                {
                    key: `/${role}/secretaries`,
                    icon: <TeamOutlined />,
                    label: t('secretaries')
                },
                {
                    key: `/${role}/drugs`,
                    icon: <MedicineBoxOutlined />,
                    label: t('drugs')
                },
                {
                    key: `/${role}/analytics`,
                    icon: <BarChartOutlined />,
                    label: t('analytics') || 'Analytics'
                },
                {
                    key: `/${role}/accounting`,
                    icon: <DollarOutlined />,
                    label: t('accounting') || 'Accounting'
                },
                {
                    key: `/${role}/sick-leave`,
                    icon: <FileProtectOutlined />,
                    label: t('sickLeave') || 'Sick Leave'
                },
                {
                    key: `/${role}/surgery`,
                    icon: <ScissorOutlined />,
                    label: t('surgery') || 'Surgery'
                },
                ...(dentalClinic ? [{
                    key: `/${role}/dental`,
                    icon: <DollarOutlined />,
                    label: t('dentalClinic')
                }] : []),
                {
                    key: `/${role}/medical-report`,
                    icon: <FileTextOutlined />,
                    label: t('medicalReports') || 'Medical Reports'
                },
                {
                    key: `/${role}/legacy-records`,
                    icon: <HistoryOutlined />,
                    label: t('legacyRecords')
                },
                {
                    key: `/${role}/data`,
                    icon: <DatabaseOutlined />,
                    label: t('dataAndBackup')
                },
                // AI Diagnosis is only visible to doctors the super admin has activated.
                ...(user?.aiEnabled ? [{
                    key: `/${role}/ai-diagnosis`,
                    icon: <ExperimentOutlined />,
                    label: t('aiDiagnosis') || 'AI Diagnosis'
                }] : []),
                // The clinic letterhead, the consultation fee, which fields the
                // visit form shows, and which lab tests this doctor orders all
                // live here. It was commented out, so the only way in was to type
                // the URL - and a doctor who cannot reach this screen cannot
                // configure any of it.
                {
                    key: `/${role}/settings`,
                    icon: <SettingOutlined />,
                    label: t('settings')
                }
            ];
        }

        if (role === 'medicalseller') {
            return [
                ...baseItems,
                {
                    key: `/${role}/doctors`,
                    icon: <UserOutlined />,
                    label: t('doctors')
                },
                {
                    key: `/${role}/reports`,
                    icon: <FileTextOutlined />,
                    label: t('reports')
                }
            ];
        }

        if (role === 'companyseller') {
            return [
                ...baseItems,
                {
                    key: `/${role}/doctors`,
                    icon: <UserOutlined />,
                    label: t('doctors')
                }
            ];
        }

        if (role === 'hospitaladmin') {
            return [
                ...baseItems,
                {
                    key: `/${role}/branches`,
                    icon: <BankOutlined />,
                    label: t('branches') || 'Branches'
                },
                {
                    key: `/${role}/reports`,
                    icon: <FileTextOutlined />,
                    label: t('doctorReport')
                }
            ];
        }

        if (role === 'lab') {
            return [
                {
                    key: `/${role}/tests`,
                    icon: <ExperimentOutlined />,
                    label: t('labTests') || 'Lab Tests'
                },
                {
                    key: `/${role}/catalog`,
                    icon: <FileTextOutlined />,
                    label: t('testCatalog') || 'Test Catalog'
                }
            ];
        }

        if (role === 'pharmacy') {
            return [
                {
                    key: `/${role}/prescriptions`,
                    icon: <MedicineBoxOutlined />,
                    label: t('prescriptions') || 'Prescriptions'
                }
            ];
        }

        if (role === 'optician') {
            return [
                {
                    key: `/${role}/orders`,
                    icon: <EyeOutlined />,
                    label: t('glassesOrders')
                }
            ];
        }

        if (role === 'secretary') {
            return [
                {
                    key: `/${role}/appointments`,
                    icon: <CalendarOutlined />,
                    label: t('appointments')
                },
                {
                    key: `/${role}/appointments-report`,
                    icon: <FileTextOutlined />,
                    label: t('appointmentsReport')
                },
                {
                    key: `/${role}/patients`,
                    icon: <UserOutlined />,
                    label: t('patients')
                },
                ...(dentalClinic ? [{
                    key: `/${role}/dental`,
                    icon: <DollarOutlined />,
                    label: t('dentalClinic')
                }] : [])
            ];
        }

        return baseItems;
    };

    const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

    // On a phone the header says where you are, the way an app's title bar
    // does: the name of the screen, or of the section a deeper page is in.
    const menuItems = getMenuItems();
    const currentKey = tabFor(menuItems, location.pathname);
    const screenTitle = menuItems.find((item) => item.key === currentKey)?.label
        || (location.pathname.endsWith('/profile') ? t('profile') : BRAND_NAME);

    return (
        <Layout className={isMobile ? 'app-shell app-shell-mobile' : 'app-shell'} style={{
            minHeight: '100vh',
            direction: isRTL ? 'rtl' : 'ltr',
            display: 'flex',
            flexDirection: isRTL ? 'row-reverse' : 'row'
        }}>
            <Sider
                collapsible
                collapsed={collapsed}
                onCollapse={setCollapsed}
                width={260}
                collapsedWidth={isMobile ? 0 : 80}
                breakpoint="lg"
                style={{
                    position: isMobile ? 'fixed' : 'relative',
                    height: isMobile ? '100vh' : 'auto',
                    left: 0,
                    zIndex: isMobile ? 999 : 'auto'
                }}
            >
                <div className="logo">
                    <BrandLogo size={collapsed ? 40 : 38} />
                    {!collapsed && <h2>{BRAND_NAME}</h2>}
                </div>
                <Menu
                    theme="dark"
                    selectedKeys={[location.pathname]}
                    mode="inline"
                    items={menuItems}
                    onClick={({ key }) => {
                        navigate(key);
                        // Close sidebar on mobile after navigation
                        if (isMobile) {
                            setCollapsed(true);
                        }
                    }}
                />
            </Sider>
            <Layout style={{ flex: 1 }}>
                <Header style={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #f5f7fa 100%)',
                    padding: isMobile ? '0 12px' : '0 40px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    boxShadow: '0 2px 12px rgba(5, 102, 141, 0.08)',
                    borderBottom: '1px solid rgba(2, 128, 144, 0.1)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '16px' }}>
                        {isMobile && (
                            <Button
                                icon={<MenuOutlined />}
                                onClick={() => setCollapsed(!collapsed)}
                                style={{
                                    border: 'none',
                                    background: 'rgba(2, 128, 144, 0.08)',
                                    color: '#028090'
                                }}
                            />
                        )}
                        {isMobile ? (
                            <div className="app-bar-title">{screenTitle}</div>
                        ) : (
                            <Avatar
                                size={48}
                                style={{
                                    background: 'linear-gradient(135deg, #028090 0%, #00A896 100%)',
                                    border: '2px solid #F0F3BD',
                                    fontWeight: 700,
                                    fontSize: '20px'
                                }}
                            >
                                {user?.name?.charAt(0).toUpperCase()}
                            </Avatar>
                        )}
                        {!isMobile && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <div style={{
                                    margin: 0,
                                    color: '#05668D',
                                    fontWeight: 700,
                                    fontSize: '16px',
                                    lineHeight: '1.3',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {user?.name}
                                </div>
                                <span style={{
                                    fontSize: '11px',
                                    color: '#00A896',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    lineHeight: '1'
                                }}>
                                    {role}
                                </span>
                            </div>
                        )}
                    </div>
                    <Space size={isMobile ? "small" : "middle"}>
                        {!isMobile && (
                            <Badge count={0} showZero={false}>
                                <Button
                                    icon={<BellOutlined style={{ fontSize: '18px' }} />}
                                    shape="circle"
                                    size="large"
                                    style={{
                                        border: 'none',
                                        background: 'rgba(2, 128, 144, 0.08)',
                                        color: '#028090'
                                    }}
                                />
                            </Badge>
                        )}
                        <Dropdown overlay={languageMenu} trigger={['click']}>
                            <Button
                                icon={<GlobalOutlined />}
                                style={{
                                    borderRadius: '8px',
                                    border: '1px solid #00A896',
                                    color: '#028090',
                                    fontWeight: 500,
                                    fontSize: isMobile ? '12px' : '14px',
                                    padding: isMobile ? '4px 8px' : '4px 15px'
                                }}
                            >
                                {i18n.language.toUpperCase()}
                            </Button>
                        </Dropdown>
                        <Dropdown
                            overlay={
                                <Menu
                                    items={[
                                        {
                                            key: 'profile',
                                            icon: <UserOutlined />,
                                            label: 'Profile',
                                            onClick: () => navigate(`/${role}/profile`)
                                        },
                                        {
                                            type: 'divider'
                                        },
                                        {
                                            key: 'logout',
                                            icon: <LogoutOutlined />,
                                            label: t('logout'),
                                            danger: true,
                                            onClick: handleLogout
                                        }
                                    ]}
                                />
                            }
                            trigger={['click']}
                        >
                            <Button
                                icon={<UserOutlined />}
                                style={{
                                    borderRadius: '8px',
                                    border: '1px solid #028090',
                                    color: '#028090',
                                    fontWeight: 500,
                                    padding: isMobile ? '4px 8px' : '0 16px',
                                    height: isMobile ? '32px' : '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    fontSize: isMobile ? '12px' : '14px'
                                }}
                            >
                                {!isMobile && 'Account'}
                            </Button>
                        </Dropdown>
                    </Space>
                </Header>
                <OfflineBanner />
                <Content style={{
                    margin: isMobile ? '12px' : '24px',
                    padding: 0,
                    background: 'transparent',
                    minHeight: 280
                }}>
                    <Outlet />
                </Content>
                {isMobile && (
                    <MobileTabBar
                        items={menuItems}
                        pathname={location.pathname}
                        moreOpen={!collapsed}
                        moreLabel={t('more')}
                        onNavigate={(key) => {
                            navigate(key);
                            setCollapsed(true);
                        }}
                        onMore={() => setCollapsed(!collapsed)}
                    />
                )}
            </Layout>
        </Layout>
    );
};

export default MainLayout;
